import { on } from "@ngrx/store";

import { cloneDeep } from "lodash";

import { CalendarEvent_LocationFragment, JobDetailsV2Fragment, Job_CalendarEventFragment } from '../../../../../generated/graphql.generated';
import { dayjs } from '../../../../core/dayjs/dayjs';
import { defaultEventTypeInfoMapV2, EventLocationTypes, eventTypeInfoMapV2 } from '../../../../global.constants';
import { BookingScheduleActions } from "../../../../schedules/booking-schedule/booking-schedule.actions";
import { MS_ONE_SECOND } from '../../../../time';

import { EventItineraryActions } from "./event-itinerary/event-itinerary.actions";
import { BookEventDialogActions } from "./timeline-book-event.actions";
import { TimelineBookEventState , timelineBookEventInitialState } from "./timeline-book-event.state";

export const reloadWindowsTriggers = [
  // dateSelected is fired when the component is initially loaded, so we don't need to listen to it
  BookEventDialogActions.dialogOpened,

  // time changes
  BookEventDialogActions.dateSelected,
  BookingScheduleActions.monthDateClicked,
  BookingScheduleActions.todayButtonClicked,
  BookingScheduleActions.previousButtonClicked,
  BookingScheduleActions.nextButtonClicked,

  // Update if we try to reschedule and after we book the event
  BookEventDialogActions.rescheduleButtonClicked,
  BookEventDialogActions.bookEventSuccess,
  BookEventDialogActions.bookEventError,

  // Update if restrictions change
  BookingScheduleActions.toggleRestrictionsButtonClicked,
  BookingScheduleActions.enableSchedulingRestrictionsButtonClicked,
  BookingScheduleActions.overrideSchedulingRestrictionsButtonClicked,

  // event itinerary changes
  EventItineraryActions.includeDockTravelTimeCheckboxToggled,
  EventItineraryActions.estimatedTimeAtLocationInputChanged,
  EventItineraryActions.travelTimeToNextLocationInputChanged,
];

export const bookEventDialogReducers = [
  on(BookEventDialogActions.dialogOpened, (state: TimelineBookEventState, {
    event, job,
  }): TimelineBookEventState => {

    const bookEventDialog = { ...cloneDeep(state.bookEventDialog) };

    bookEventDialog.eventId = event.id;

    bookEventDialog.assetIds = (event.assets || [])
      .map((a) => a.id);

    const isScheduled = ![ 'required', 'pending' ].includes(event.status);

    const dockStart = (event.locations || [])
      .find((l) => l.type === EventLocationTypes.dockStart);

    bookEventDialog.scheduleMode = isScheduled ? 'view' : 'schedule';

    bookEventDialog.pinStartTime =
      Boolean(eventTypeInfoMapV2[event.type]?.pinDefaultStartTime);

    const startDayjs = getStartDayjs({ job, event, isScheduled, dockStart });

    bookEventDialog.time = startDayjs.format('h:mm A');

    bookEventDialog.date = startDayjs.format('YYYY-MM-DD');

    return {
      ...state,
      bookEventDialog,
    }
  }),
  on(
    BookEventDialogActions.closeBookDialogButtonClicked,
    BookEventDialogActions.escapeKeyPressed,
    (state: TimelineBookEventState) : TimelineBookEventState => {
      return {
        ...state,
        ...timelineBookEventInitialState,
      }
    }
  ),
  on(EventItineraryActions.saveEventChangesSuccess, (state: TimelineBookEventState, { promptedBy }) : TimelineBookEventState => {

    if (promptedBy.type !== BookEventDialogActions.saveAsButtonClicked.type) { return state; }

    return {
      ...state,
      ...timelineBookEventInitialState,
    }
  }),
  on(BookingScheduleActions.enableSchedulingRestrictionsButtonClicked, (state: TimelineBookEventState): TimelineBookEventState => ({
    ...state,
    bookEventDialog: {
      ...state.bookEventDialog,
      restrictionsDisabled: false,
    },
  })),
  on(BookingScheduleActions.overrideSchedulingRestrictionsButtonClicked, (state: TimelineBookEventState): TimelineBookEventState => ({
    ...state,
    bookEventDialog: {
      ...state.bookEventDialog,
      restrictionsDisabled: true,
    },
  })),
  on(BookEventDialogActions.findAvailabilitySuccess, (state: TimelineBookEventState, { windows, timezone }): TimelineBookEventState => {

    const formattedWindows = windows.map((w) => ({
      ...w,
      times: w.startTimes.map((t) => dayjs.tz(t * MS_ONE_SECOND, timezone).format('h:mm A')),
    }));

    const canBook = formattedWindows.length > 0 || state.bookEventDialog.restrictionsDisabled;

    // If time no longer available, reset and show warning
    const availableTimes = formattedWindows.flatMap((w) => w.times)

    let time = state.bookEventDialog.time;
    let showTimeNotAvailableWarning = false;

    if (time && !availableTimes.includes(time)) {
      if (!state.bookEventDialog.pinStartTime) {
        time = undefined;
      }
      showTimeNotAvailableWarning = canBook || state.bookEventDialog.pinStartTime;
    }

    // If asset no longer available, remove and show warning
    const availableWindows = formattedWindows.filter((w) => !time || w.times.includes(time));

    const availableAssets = availableWindows.flatMap((w) => w.assets);

    const assetIds = state.bookEventDialog.assetIds
      .filter((assetId) => availableAssets.some((a) => a.id === assetId));

    const showAssetsNotAvailableWarning = canBook && assetIds.length < state.bookEventDialog.assetIds.length;

    return {
    ...state,
    bookEventDialog: {
      ...state.bookEventDialog,
      windows,
      time,
      showTimeNotAvailableWarning,
      assetIds,
      showAssetsNotAvailableWarning,
    },
  }
  }),
  on(BookEventDialogActions.timeWithTimezoneSelected, (state: TimelineBookEventState, { time, timezone }): TimelineBookEventState => {

    const formattedWindows = state.bookEventDialog.windows.map((w) => ({
      ...w,
      times: w.startTimes.map((t) => dayjs.tz(t * MS_ONE_SECOND, timezone).format('h:mm A')),
    }))

    const noAvailability = !formattedWindows.length && !state.bookEventDialog.restrictionsDisabled;

      // If assets no longer available, remove and show warning
    const availableWindows = formattedWindows.filter((w) => !time || w.times.includes(time));

    const availableAssets = availableWindows.flatMap((w) => w.assets);

    const assetIds = state.bookEventDialog.assetIds
      .filter((assetId) => availableAssets.some((a) => a.id === assetId));

    const showAssetsNotAvailableWarning = !noAvailability && assetIds.length < state.bookEventDialog.assetIds.length;

    return {
    ...state,
    bookEventDialog: {
      ...state.bookEventDialog,
      time,
      assetIds,
      showAssetsNotAvailableWarning,
    }
  }
  }),
  on(BookEventDialogActions.assetsSelected, (state: TimelineBookEventState, { assetIds }): TimelineBookEventState => {
    return {
      ...state,
      bookEventDialog: {
        ...state.bookEventDialog,
        assetIds,
      }
    }
  }),
  on(
    BookEventDialogActions.bookEventSuccess,
    BookEventDialogActions.rescheduleEventSuccess,
    (state: TimelineBookEventState): TimelineBookEventState => {
      return {
        ...state,
        bookEventDialog: {
          ...state.bookEventDialog,
          scheduleMode: 'view',
        },
      }
    },
  ),
  on(BookEventDialogActions.rescheduleButtonClicked, (state: TimelineBookEventState): TimelineBookEventState => {
    return {
      ...state,
      bookEventDialog: {
        ...state.bookEventDialog,
        scheduleMode: 'schedule',
      },
    }
  }),
  on(BookingScheduleActions.toggleRestrictionsButtonClicked, (state: TimelineBookEventState): TimelineBookEventState => ({
    ...state,
    bookEventDialog: {
      ...state.bookEventDialog,
      restrictionsDisabled: !state.bookEventDialog.restrictionsDisabled,
    },
  })),
  on(BookingScheduleActions.datesSet, (state: TimelineBookEventState, { start, view }): TimelineBookEventState => {

    if (view !== 'resourceTimelineDay') return state;

    return {
      ...state,
      bookEventDialog: {
        ...state.bookEventDialog,
        date: start,
      }
    }
  }),
]

function getStartDayjs(
  input: {
    job: JobDetailsV2Fragment;
    event: Job_CalendarEventFragment;
    dockStart: CalendarEvent_LocationFragment;
    isScheduled: boolean;
  }
) {

  const {
    job,
    event,
    isScheduled,
    dockStart,
  } = input;

  const timezone = job.zone.timezone;

  let eventStart = event.start;

  const forceDefaultLocationTimes = Boolean(eventTypeInfoMapV2[event.type]?.forceDefaultLocationTimes);

  if (eventStart && isScheduled && dockStart && !forceDefaultLocationTimes) {
    eventStart += dockStart.travelTimeToNextLocation || 0;
    eventStart += dockStart.travelTimeToNextLocationOffset || 0;
    eventStart += dockStart.estimatedTimeAtLocation || 0;
  }

  if (eventStart) {
    return dayjs.tz(eventStart * 1000, timezone);
  }

  const today = dayjs.tz(dayjs(), timezone);

  const bookingStartTime =
    eventTypeInfoMapV2[event.type]?.bookingStartTime
    ?? defaultEventTypeInfoMapV2[event.type]?.bookingStartTime;

  const configuredOffset =
    eventTypeInfoMapV2[event.type]?.bookingDateOffset
    ?? defaultEventTypeInfoMapV2[event.type]?.bookingDateOffset;

  if (typeof configuredOffset === 'number' && Number.isInteger(configuredOffset) && configuredOffset >= 0) {
    return setTime(today.add(configuredOffset, 'day'), bookingStartTime);
  }

  // null, undefined, negative, non-integer, or any non-numeric value → default logic
  const twoDaysFromToday = setTime(today.add(2, 'day'), bookingStartTime);

  if (!job.timeline) { return twoDaysFromToday; }

  const timeline = setTime(dayjs.tz(job.timeline, timezone), bookingStartTime);

  const daysFromToday = timeline.diff(today, 'day');

  if (daysFromToday < 1 || daysFromToday > 364) { return twoDaysFromToday; }

  return timeline;
}

function setTime(date: dayjs.Dayjs, startTime?: string) {
  if (typeof startTime === 'string') {
    const [ hour, minute ] = startTime.split(':').map(Number);
    if (Number.isInteger(hour) && hour >= 0 && hour <= 23 &&
        Number.isInteger(minute) && minute >= 0 && minute <= 59) {
      return date.hour(hour).minute(minute).second(0).millisecond(0);
    }
  }
  return date.hour(8).minute(0).second(0).millisecond(0);
}
